import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Check,
  Clock,
  Headphones,
  Phone,
  Upload,
  UserPlus,
  Wallet,
  X,
} from "lucide-react";
import StatusBadge from "./StatusBadge";
import { formatCurrency, formatDateTime } from "../../data/mock/dashboardData";
import { formatSlaMinutes } from "./SlaPerformancePanel";

const STAGE_ICONS = {
  uploaded: Upload,
  assigned: UserPlus,
  contacted: Phone,
  payment_initiated: Wallet,
  payment_completed: Check,
};

function formatDurationSeconds(value) {
  if (value == null || value === "") return "—";
  const secs = Number(value);
  if (!Number.isFinite(secs)) return String(value);
  const m = Math.floor(secs / 60);
  const s = Math.abs(Math.round(secs % 60));
  return `${m}:${String(s).padStart(2, "0")}`;
}

function StageRow({ stage }) {
  const Icon = STAGE_ICONS[stage.key] || Clock;
  const isDone = stage.state === "done";
  const isCurrent = stage.state === "current";

  return (
    <li className="relative flex gap-3 pb-5 last:pb-0">
      <div className="flex flex-col items-center">
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1 ${
            isDone
              ? "bg-emerald-50 text-emerald-700 ring-emerald-600/15"
              : isCurrent
                ? "bg-accent/15 text-accent-dark ring-accent/25"
                : "bg-primary/[0.04] text-primary/35 ring-primary/10"
          }`}
        >
          {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
        </span>
        <span className="mt-1 w-px flex-1 bg-primary/[0.08]" aria-hidden />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-sm font-bold text-primary">{stage.label}</p>
          <p className="text-[11px] font-medium text-primary/40">
            {stage.at ? formatDateTime(stage.at) : "Pending"}
          </p>
        </div>
        <p className="mt-0.5 text-xs leading-relaxed text-primary/55">{stage.description}</p>
        {stage.lag_minutes != null ? (
          <p className="mt-1 text-[11px] font-semibold text-primary/45">
            {stage.lag_label}: {formatSlaMinutes(stage.lag_minutes)}
          </p>
        ) : null}
        {isCurrent && !stage.at ? (
          <span className="mt-1.5 inline-flex rounded-md bg-accent/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent-dark">
            Current stage
          </span>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Case SLA timeline drawer — upload → assign → contact → payment (+ call durations).
 */
export default function SlaTimelineDrawer({
  open,
  onClose,
  caseId,
  fetchTimeline,
  portalLabel = "Case",
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !caseId || !fetchTimeline) {
      setData(null);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchTimeline(caseId)
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err?.message || "Could not load SLA timeline.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, caseId, fetchTimeline]);

  const caseRow = data?.case;
  const stages = Array.isArray(data?.stages) ? data.stages : [];
  const calls = data?.calls || {};
  const callItems = Array.isArray(calls.items) ? calls.items : [];
  const timeline = data?.timeline || {};

  return (
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-[60] flex justify-end">
          <motion.button
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="absolute inset-0 bg-primary-dark/45 backdrop-blur-[2px]"
            aria-label="Close SLA timeline"
            onClick={onClose}
          />

          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 340, mass: 0.85 }}
            className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
            role="dialog"
            aria-labelledby="sla-drawer-title"
          >
            <div className="flex items-start justify-between gap-3 border-b border-primary/[0.08] bg-gradient-to-r from-primary/[0.03] to-accent/[0.05] px-5 py-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/[0.08] text-primary">
                    <Clock className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <h2 id="sla-drawer-title" className="text-base font-bold text-primary">
                      SLA timeline
                    </h2>
                    <p className="truncate text-xs text-primary/45">
                      {caseRow?.case_id || caseId || portalLabel}
                      {caseRow?.customer_name ? ` · ${caseRow.customer_name}` : ""}
                    </p>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary/10 text-primary/50 hover:bg-primary/[0.04] hover:text-primary"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loading ? (
                <p className="py-16 text-center text-sm text-primary/45">Loading SLA timeline…</p>
              ) : error ? (
                <p className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-800">
                  {error}
                </p>
              ) : !data ? (
                <p className="py-16 text-center text-sm text-primary/45">No SLA data for this case.</p>
              ) : (
                <>
                  <section className="mb-5 rounded-xl border border-primary/[0.07] bg-primary/[0.02] p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={caseRow?.status || "open"} />
                      <span className="rounded-md bg-white px-2 py-0.5 text-[11px] font-semibold text-primary/60 ring-1 ring-primary/10">
                        {String(timeline.current_stage || "uploaded").replaceAll("_", " ")}
                      </span>
                      {data?.digestion?.assign_breached ? (
                        <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-amber-200">
                          Assign SLA breached
                        </span>
                      ) : null}
                      {data?.digestion?.contact_breached ? (
                        <span className="rounded-md bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-800 ring-1 ring-rose-200">
                          Contact SLA breached
                        </span>
                      ) : null}
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <dt className="text-primary/40">Owed</dt>
                        <dd className="font-bold text-primary">
                          {formatCurrency(caseRow?.amount_owed || 0)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-primary/40">Outstanding</dt>
                        <dd className="font-bold text-primary">
                          {formatCurrency(caseRow?.outstanding || 0)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-primary/40">Agent</dt>
                        <dd className="font-semibold text-primary/80">
                          {caseRow?.agent_name || "Unassigned"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-primary/40">Elapsed</dt>
                        <dd className="font-semibold text-primary/80">
                          {formatSlaMinutes(timeline.elapsed_minutes)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-primary/40">Assign target</dt>
                        <dd className="font-semibold text-primary/80">
                          {data?.targets?.assign_within_days ?? 5}d
                          {data?.digestion?.upload_to_assign_days != null
                            ? ` · took ${data.digestion.upload_to_assign_days}d`
                            : ""}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-primary/40">Contact target</dt>
                        <dd className="font-semibold text-primary/80">
                          {data?.targets?.first_contact_within_days ?? 2}d
                          {data?.digestion?.assign_to_contact_days != null
                            ? ` · took ${data.digestion.assign_to_contact_days}d`
                            : ""}
                        </dd>
                      </div>
                    </dl>
                    {caseRow?.portfolio_group_name ? (
                      <p className="mt-2 truncate text-[11px] text-primary/45">
                        Batch: {caseRow.portfolio_group_name}
                      </p>
                    ) : null}
                  </section>

                  <section className="mb-6">
                    <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-primary/40">
                      Recovery stages
                    </h3>
                    <ol>{stages.map((stage) => <StageRow key={stage.key} stage={stage} />)}</ol>
                  </section>

                  <section>
                    <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-primary/40">
                      Call activity
                    </h3>
                    <div className="mb-3 grid grid-cols-3 gap-2">
                      <div className="rounded-xl border border-primary/[0.07] bg-white px-3 py-2.5">
                        <p className="text-[10px] font-semibold uppercase text-primary/40">Calls</p>
                        <p className="mt-0.5 text-sm font-bold text-primary">{calls.count ?? 0}</p>
                      </div>
                      <div className="rounded-xl border border-primary/[0.07] bg-white px-3 py-2.5">
                        <p className="text-[10px] font-semibold uppercase text-primary/40">Total talk</p>
                        <p className="mt-0.5 text-sm font-bold text-primary">
                          {formatDurationSeconds(calls.total_duration_seconds)}
                        </p>
                      </div>
                      <div className="rounded-xl border border-primary/[0.07] bg-white px-3 py-2.5">
                        <p className="text-[10px] font-semibold uppercase text-primary/40">Avg</p>
                        <p className="mt-0.5 text-sm font-bold text-primary">
                          {formatDurationSeconds(calls.avg_duration_seconds)}
                        </p>
                      </div>
                    </div>

                    {callItems.length === 0 ? (
                      <p className="rounded-xl border border-dashed border-primary/10 px-4 py-6 text-center text-xs text-primary/40">
                        No call logs yet for this case.
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {callItems.map((item) => (
                          <li
                            key={item.id}
                            className="rounded-xl border border-primary/[0.07] bg-white px-3 py-2.5"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-primary">
                                  {item.channel || "Call"}
                                  {item.outcome ? ` · ${item.outcome}` : ""}
                                </p>
                                <p className="mt-0.5 text-[11px] text-primary/45">
                                  {item.agent || "Agent"}
                                  {item.logged_at
                                    ? ` · ${formatDateTime(item.logged_at)}`
                                    : ""}
                                </p>
                              </div>
                              <span className="inline-flex shrink-0 items-center gap-1 tabular-nums text-xs font-bold text-primary/70">
                                <Headphones className="h-3.5 w-3.5 text-primary/30" />
                                {formatDurationSeconds(item.duration_seconds)}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </>
              )}
            </div>
          </motion.aside>
        </div>
      ) : null}
    </AnimatePresence>
  );
}
